<template>
  <v-dialog
    :value="visiblePopup"
    :max-width="530"
    @click:outside="onOkClicked"
    @keydown.esc="onOkClicked"
  >
    <v-card class="popup-wrapper">
      <div class="title">{{ title }}</div>
      <div class='details-section'>
        <div class='details-section__date-details'>
          <div class="details-section__date-details__initiated-date">
            <div class='sub-title'>
              {{ $t('activity.details.popup.initiated.date.subtitle') }}
            </div>
            <div class='value'>
              {{ initiatedDate }}
            </div>
          </div>
          <div class="details-section__date-details__failed-date">
            <div class='sub-title'>
              {{ finishedDateTitle }}
            </div>
            <div class='value'>
              {{ failedDate }}
            </div>
          </div>
        </div>
        <div class='details-section__activity-action'>
          <div class='sub-title'>
            {{ $t('activity.details.popup.action.subtitle') }}
          </div>
          <div class='value'>
            {{ activityAction }}
          </div>
        </div>
        <div class='details-section__asset-details'>
          <div class="details-section__asset-details__tld">
            <div class='sub-title'>
              {{ $t('activity.details.popup.tld.subtitle') }}
            </div>
            <div class='value'>
              {{ manualConfigurationTld }}
            </div>
          </div>
          <div class="details-section__asset-details__asset">
            <div class='sub-title'>
              {{ $t('activity.details.popup.asset.subtitle') }}
            </div>
            <div class='value'>
              {{ manualConfigurationAsset }}
            </div>
          </div>
        </div>
        <div class="failure-reason">
          <div class='sub-title'>
            {{ failureReasonTitle }}
          </div>
          <div class='value'>
            {{ failureReason }}
          </div>
        </div>
      </div>
      <div class="buttons">
        <v-btn small depressed class="ok-button btn-primary" @click="onOkClicked()">
          {{ $t('confirm.ok') }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>

</template>

<script>

import moment from 'moment';
import { projectStatuses } from '@/constants/projects';
import { ActivityFinishedStatus } from '@/constants/activity';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

const ACTIVITY_DETAILS_DATE_FORMAT = 'DD MMM YYYY hh:mm';

export default {
  name: 'ActivityDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
    activity: {
      type: Object,
      required: true,
    },
  },
  methods: {
    onOkClicked() {
      this.$emit('clicked');
    },
  },
  computed: {
    manualConfigurationData() {
      return this.metaData.manualConfigurationData || {};
    },
    manualConfigurationAsset() {
      return this.manualConfigurationData.asset || 'N/A';
    },
    manualConfigurationTld() {
      return this.manualConfigurationData.tld || this.metaData.tld || 'N/A';
    },
    activityAction() {
      return this.metaData.integrationAction || 'N/A';
    },
    isWarningActivity() {
      return this.metaData?.integrationFinishedStatus === ActivityFinishedStatus.FAILED;
    },
    isFailedActivity() {
      return this.activity.status === projectStatuses.FAILED;
    },
    finishedDateTitle() {
      return this.isFailedActivity ? this.$t('activity.details.popup.failed.date.subtitle') : this.$t('activity.details.popup.finished.date.subtitle');
    },
    title() {
      return this.isFailedActivity ? this.$t('activity.details.popup.title.failed') : this.$t('activity.details.popup.title.warning');
    },
    metaData() {
      return this.activity.metaData || {};
    },
    initiatedDate() {
      return moment(this.activity.createdDate).format(ACTIVITY_DETAILS_DATE_FORMAT);
    },
    failedDate() {
      return moment(this.activity.updatedDate).format(ACTIVITY_DETAILS_DATE_FORMAT);
    },
    failureReasonTitle() {
      return this.isFailedActivity ? this.$t('activity.details.popup.failed.reason.subtitle') : this.$t('activity.details.popup.warning.subtitle');
    },
    failureReason() {
      return this.isFailedActivity ? (this.metaData.failureReason || this.$t('activity.details.popup.failure.reason.default')) : this.$t('activity.details.popup.warning.reason.default');
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  max-width: rem(580);
  padding: $space-8 $space-10;

  .sub-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: $space-2;
    text-transform: uppercase;
    margin-bottom: $space-1-5;
  }

  .value {
    font-size: $font-size-base;
    line-height: 1.36;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;
  }

  .details-section {
    display: flex;
    flex-direction: column;

    &__date-details {
      display: flex;
      margin-bottom: $space-9;

      &__initiated-date {
        display: flex;
        flex-direction: column;
        margin-right: rem(85);
      }

      &__failed-date {
        display: flex;
        flex-direction: column;
      }
    }

    &__activity-action {
      margin-bottom: $space-9;
      display: flex;
      flex-direction: column;

      .value {
        text-transform: capitalize;
      }
    }

    &__asset-details {
      margin-bottom: $space-9;
      display: flex;

      &__tld {
        display: flex;
        flex-direction: column;
        margin-right: rem(85);
      }

      &__asset {
        display: flex;
        flex-direction: column;
      }
    }

    .failure-reason {
      .value {
        text-transform: capitalize;
      }
    }
  }

  .title {
    color: var(--text-primary);
    font-size: $font-size-base;
    font-weight: 600;
    letter-spacing: 1.12px;
    line-height: $space-4;
    text-transform: uppercase;
    padding-bottom: $space-6;
  }

  .message {
    font-size: $font-size-base;
    line-height: 1.36;
    font-weight: normal;
    color: var(--text-primary);
    letter-spacing: 0;
  }

  .buttons {
    text-align: right;
    padding-top: $space-7;

    .ok-button {
      height: $btn-height-md;
      width: rem(51);
      border-radius: $radius-sm;
      margin-left: $space-2;
    }
  }
}
</style>
